import type { PayNotePart } from '../../lib/payroll'

/** A pay line shows a non-zero amount, and a 0 whose note says why (a fixed salary's lapsed or settled leave). */
export function amountOrNoteLine(
  pay: number | string | null | undefined,
  note: string | null | undefined,
): { amount: number; signed: boolean } | null {
  const amount = Number(pay ?? 0)
  if (amount !== 0) return { amount, signed: true }
  return (note ?? '').trim() ? { amount: 0, signed: false } : null
}

export function clYearEndLine(row: {
  cl_year_end_pay?: number | string | null
  cl_year_end_note?: string | null
}): { amount: number; signed: boolean } | null {
  return amountOrNoteLine(row.cl_year_end_pay, row.cl_year_end_note)
}

export function clYearEndValue(line: { amount: number; signed: boolean }, money: (value: number) => string): string {
  return `${line.signed ? '+ ' : ''}${money(line.amount)}`
}

type Translate = (key: string, params?: Record<string, string>) => string

/** Literal `t()` calls so `i18n/keys.test.ts` sees every key; an unknown key is null, so the caller keeps the sent English. */
function payNoteSentence(key: string, params: Record<string, string>, t: Translate): string | null {
  switch (key) {
    case 'psl_priced':
      return t('payroll.payNote.pslPriced', params)
    case 'psl_lapse_fixed':
      return t('payroll.payNote.pslLapseFixed', params)
    case 'break_net_zero':
      return t('payroll.payNote.breakNetZero', params)
    case 'break_unpaid_fixed':
      return t('payroll.payNote.breakUnpaidFixed', params)
    case 'break_unpaid':
      return t('payroll.payNote.breakUnpaid', params)
    default:
      return null
  }
}

/** A why-note in the reader's language; any sentence that cannot be translated sends the whole note as it was stored. */
export function payNoteText(
  note: string | null | undefined,
  parts: readonly PayNotePart[] | null | undefined,
  t: Translate,
): string {
  const stored = (note ?? '').trim()
  if (!parts?.length) return stored
  const sentences = parts.map((part) => ('key' in part ? payNoteSentence(part.key, part.params, t) : part.text))
  return sentences.every((sentence) => sentence !== null) ? sentences.join('; ') : stored
}

/** A pay line's label with its why-note; no note, no brackets. */
export function labelWithNote(label: string, note: string): string {
  return note ? `${label} (${note})` : label
}
